import React, { useEffect, useMemo, useState } from 'react'
import { clamp, cn, getUserType, toCamelCase } from '@utils/safeTs'
import styles from './FlussoPersonalizzazioneCaricamentoBozzePosizione.module.sass'
import { Button, CronologiaButton, FormField, FormInput, FormTextarea, StatoOrdine, Flag, FormFile, PosizionePersonalizzazioneOrdineStatus, MyAccountImagePreview, Alert } from '@components/atoms'
import { ArrowRight, ChevronDown, Upload, Edit, Bin, Info } from '@components/icons'
import { OrdineDetailType, PersonalizzazioneOrdineType, PosizionePersonalizzazioneType, RootStateType } from '@utils/types'
import { useSelector } from 'react-redux'
import useTrans from '@hooks/useTrans'
import MyAccountStatusLogModal from '../MyAccountStatusLogModal/MyAccountStatusLogModal'
import { POSIZIONIPERSONALIZZAZIONEORDINE } from '@utils/endpoint'
import { api } from '@utils/index'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import FlussoPersonalizzazionePosizione from '../FlussoPersonalizzazionePosizione/FlussoPersonalizzazionePosizione'
import FlussoPersonalizzazionePosizioneConfigurazione from '../FlussoPersonalizzazionePosizioneConfigurazione/FlussoPersonalizzazionePosizioneConfigurazione'
import { EDITOR_SCOPE_ORDER, getEditorUrl } from '@utils/editor-bozze'
import { useRouter } from 'next/router'
import FlussoPersonalizzazioneNotaValue from '@components/atoms/FlussoPersonalizzazioneNotaValue/FlussoPersonalizzazioneNotaValue'
import FlussoPersonalizzazionePosizioneEditorInfo from '../FlussoPersonalizzazionePosizioneEditorInfo/FlussoPersonalizzazionePosizioneEditorInfo'

export const getIsEditable = (pos, userType) =>
  ['BOZZA_DA_CARICARE', 'BOZZA_SALVATA', 'BOZZA_RIFIUTATA'].includes(pos.statoPosizionePersonalizzazione) &&
  ['admin', 'fornitore'].includes(userType)

export const getNextPosizioneEditabile = (
  ordini,
  posizione,
  userType,
  fromTop = false,
  skipBozzeSalvate = false
) => {
  // Scorro tutti gli ordini e da dopo la posizione corrente guardo quali altre sono editabili
  let found = fromTop

  for (const o of ordini) {
    for (const p of o.personalizzazioniOrdine) {
      for (const pos of p.posizioniPersonalizzazioneOrdine) {
        // Se ho già ricominciato da capo mi fermo appena torno alla pos corrente
        if (fromTop && pos.id === posizione.id) return null
        if (
          found &&
          getIsEditable(pos, userType) &&
          (!skipBozzeSalvate || pos.statoPosizionePersonalizzazione !== 'BOZZA_SALVATA')
        ) {
          return { ...pos, personalizzazioneId: p.id }
        }
        if (pos.id === posizione.id) {
          found = true
        }
      }
    }
  }
  // se non l'ho trovata dopo riparto dall'inizio e prendo la prima disponibile
  if (!fromTop) {
    return getNextPosizioneEditabile(ordini, posizione, userType, true, skipBozzeSalvate)
  }
  return null
}

interface Props {
  ordini: OrdineDetailType[]
  ordine: OrdineDetailType
  posizione: PosizionePersonalizzazioneType
  personalizzazione: PersonalizzazioneOrdineType
  setPosizione: (p: PosizionePersonalizzazioneType) => void
  setStatus: ((v: '' | 'unsaved' | 'completed') => void)
  personalizzazioniOpen?: number[]
  setPersonalizzazioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  posizioniOpen: number[]
  setPosizioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  className?: string
}

const FlussoPersonalizzazioneCaricamentoBozzePosizione = (props: Props) => {

  const { 
    ordini,
    ordine,
    posizione,
    personalizzazione,
    setPosizione,
    setStatus,
    personalizzazioniOpen,
    setPersonalizzazioniOpen,
    posizioniOpen,
    setPosizioniOpen,
    className = '',
  } = props
  
  const t = useTrans()
  const router = useRouter()
  const hashParam = Array.isArray(router.query?.hash) ? router.query.hash[0] : router.query?.hash
  const { utente } = useSelector((state: RootStateType) => state.utente)
  const userType = getUserType(utente)

  const isEditable = useMemo(() => getIsEditable(posizione, userType), [userType, posizione])
  const loghi = useMemo(() => {
    const loghiEditor = posizione.editorState?.loghi?.map((logo) => logo.fileOriginale).filter(Boolean) || []
    const logoGiaNellEditor = posizione.logo && loghiEditor.some((logo) => logo.name === posizione.logo.name)

    return logoGiaNellEditor
      ? loghiEditor
      : [...(posizione.logo ? [posizione.logo] : []), ...loghiEditor]
  }, [posizione])

  const [logOpen, setLogOpen] = useState(false)
  const [edited, setEdited] = useState(false)
  const [saving, setSaving] = useState(false)
  const [submitAction, setSubmitAction] = useState('default')
  const [bozzaEditor, setBozzaEditor] = useState(posizione.bozzaDigitaleImage ? null : posizione.bozzaDigitaleImageEditor)
  const status = useMemo(() => 
    !['BOZZA_DA_CARICARE', 'BOZZA_SALVATA', 'BOZZA_RIFIUTATA'].includes(posizione.statoPosizionePersonalizzazione)
    ? ''  
    : edited 
    ? 'unsaved' 
    : 
      // tutto obbligatorio per considerare la posizione completa (e considero quella salvata, non quella pending in values)
      posizione.bozzaDigitaleImage
      // && posizione.note // nota non obbligatoria in caricamento bozze
      && posizione.larghezza 
      && posizione.altezza 
      && posizione.pantoni.filter(x => !x.pantone).length === 0 
      ? 'completed' : ''
    , [edited, posizione])

  const getInitialValues = (p: PosizionePersonalizzazioneType) => ({
    larghezza: p.larghezza,
    altezza: p.altezza,
    pantoni: p.pantoni.map(x => x.pantone),
    note: p.note || '',
    bozzaDigitaleImage: p.statoPosizionePersonalizzazione === 'BOZZA_RIFIUTATA' && isEditable ? undefined : p.bozzaDigitaleImage || undefined,
    fileBozzaDigitaleImage: undefined,
  })   

  const initialValues = getInitialValues(posizione)

  const validationSchema = useMemo(() => {
    const parseDimension = (value) => {
      if (value === '' || value === null || value === undefined) return null

      const parsedValue = Number(`${value}`.replace(',', '.'))
      return Number.isNaN(parsedValue) ? null : parsedValue
    }

    const getDimensionSchema = (maxValue) =>
      yup.mixed().test('dimension-validation', function validateDimension(value) {
        if (maxValue === 0) { // se la dimensione è 0 non è richiesto il campo
          return true
        }

        const parsedValue = parseDimension(value)

        if (parsedValue === null) {
          return this.createError({ message: t('Campo obbligatorio') })
        }

        if (parsedValue <= 0) {
          return this.createError({ message: t('Solo numeri positivi') })
        }

        if (maxValue && parsedValue > maxValue) {
          return this.createError({ message: t('Massimo {0} cm', [maxValue]) })
        }

        return true
      })

    return yup.object().shape({
      larghezza: getDimensionSchema(Number(posizione?.dimensione?.base)),
      altezza: getDimensionSchema(Number(posizione?.dimensione?.altezza)),
      pantoni: yup.array().of(
        posizione.numeroColoriPosizione.fullColor
          ? yup.string().nullable()
          : yup
              .string()
              .trim()
              .required(t('Campo obbligatorio'))
              .max(20, t('Massimo 20 caratteri'))
              .test(
                'no-commas',
                t('Il campo non può contenere virgole'),
                (value) => !value?.includes(',')
              )
      ),
      bozzaDigitaleImage: (ordine.confermaCaricamentoBozzeDigitali
        ? yup.mixed().required(t('Campo obbligatorio'))
        : yup.mixed())
        .test('file-format', t('Formati consentiti: .jpg, .jpeg, .png'), (value) => {
          if (!value?.name) return true

          return ['jpg', 'jpeg', 'png'].includes(value.name.split('.').pop()?.toLowerCase())
        }),
    })
  }, [ordine.confermaCaricamentoBozzeDigitali, posizione])

  const getPersonalizzazioneId = (posizioneId: number) => {
    for (const o of ordini) {
      for (const p of o.personalizzazioniOrdine){
        for (const pos of p.posizioniPersonalizzazioneOrdine) {
          if (pos.id === posizioneId) {
            return p.id
          }
        }
      }
    }
    return null
  }

  const save = (v, callbackAction) => {
    setSaving(true)
    let formData = new FormData()
    Object.keys(v).forEach((k) => {
      formData.append(k, v[k])
    })
    if (bozzaEditor)
      formData.append('bozzaEditor', '1')
    api
      .put(`${POSIZIONIPERSONALIZZAZIONEORDINE}${posizione.id}/`, formData, {
        params: {
          ...(hashParam ? { hash: hashParam } : {}),
        },
      })
      .then((res) => {
        const newPosizione = toCamelCase(res.data) as PosizionePersonalizzazioneType
        setPosizione(newPosizione)
        setEdited(false)
        setBozzaEditor(newPosizione?.bozzaDigitaleImage ? null : newPosizione.bozzaDigitaleImageEditor)
        displayNotification(
          `posizione_aggiornata_${posizione.id}`, 
          t("Configurazione {0} aggiornata correttamente", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'success'
        )
        if (callbackAction === 'next'){
          const nextPos = getNextPosizioneEditabile(ordini, posizione, userType)
          if (nextPos){
          // apro la personalizzazione
          if (nextPos.personalizzazioneId && !personalizzazioniOpen.includes(nextPos.personalizzazioneId))
            setPersonalizzazioniOpen(personalizzazioniOpen.concat([nextPos.personalizzazioneId]))
          // apro la posizione
          if (!posizioniOpen.includes(nextPos.id))
            setPosizioniOpen(posizioniOpen.concat([nextPos.id]))
          setTimeout(() => {
            // Metto un timeout per dare tempo alle posizioni di aprirsi
            const el = document.getElementById(`posizione_${nextPos.id}`)
            if (el){
              el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }, 100)
          }
        }
      })
      .catch((res) => {
        displayNotification(
          `posizione_aggiornata_error_${posizione.id}`, 
          t("Errore durante l'aggiornamento della configurazione {0}", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'error'
        )
      })
      .finally(() => setSaving(false))
  }

  const onValueChange = (e, handleChange) => {
    handleChange(e)
    setEdited(true)
  }

  useEffect(() => {
    setStatus(status)
  }, [status])

  return (
    <FlussoPersonalizzazionePosizione 
      status={status}
      posizione={posizione}
      posizioniOpen={posizioniOpen}
      setPosizioniOpen={setPosizioniOpen}
      userType={userType}
      className={cn(styles.root, posizioniOpen.includes(posizione.id) ? styles['root--open'] : '', isEditable ? styles['root--editable'] : '', className)}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          save(values, submitAction)
        }}
      >
        {({ values, errors, touched, isValid, handleChange, handleBlur, setFieldValue, setFieldTouched }) => (
          <Form className={styles.form}>
            <FlussoPersonalizzazionePosizioneConfigurazione posizione={posizione} personalizzazione={personalizzazione} />
            <FlussoPersonalizzazionePosizioneEditorInfo posizione={posizione} />
            <div className={styles.row}>
              <div className={cn(styles.section, styles['section--dimensione'])}>
                <p className={styles.sectionTitle}>{t('Dimensione')}</p>
                {isEditable ? (
                  <div className={styles.fieldRow}>
                    <FormField
                      label={`${t('Larghezza')} (CM)`}
                      subtitle={`${t('Max')} ${posizione?.dimensione?.base} cm`}
                      status={touched.larghezza && errors.larghezza ? 'error' : undefined}
                      errorMessage={errors.larghezza as string}
                    >
                      <FormInput 
                        id={`${posizione.id}_larghezza`}
                        name={'larghezza'}
                        value={values.larghezza}
                        onChange={(e) => {
                          const cleanValue = e.target.value?.replace(',', '.')
                          const hasFinalComma = cleanValue.length ? cleanValue.slice(-1) === '.' : false
                          setFieldValue('larghezza', clamp(cleanValue, 0, Number(posizione?.dimensione?.base)) + (hasFinalComma ? '.' : ''))
                          setEdited(true)
                        }}
                        onBlur={handleBlur}
                        max={posizione?.dimensione?.base || 0}
                      />
                    </FormField>
                    <FormField
                      label={`${t('Altezza')} (CM)`}                
                      subtitle={`${t('Max')} ${posizione?.dimensione?.altezza} cm`}
                      status={touched.altezza && errors.altezza ? 'error' : undefined}
                      errorMessage={errors.altezza as string}
                    >
                      <FormInput 
                        id={`${posizione.id}_altezza`}
                        name='altezza'
                        value={values.altezza}
                        onChange={(e) => {
                          const cleanValue = e.target.value?.replace(',', '.')
                          const hasFinalComma = cleanValue.length ? cleanValue.slice(-1) === '.' : false
                          setFieldValue('altezza', clamp(cleanValue, 0, Number(posizione?.dimensione?.altezza)) + (hasFinalComma ? '.' : ''))
                          setEdited(true)
                        }}
                        onBlur={handleBlur}
                        max={posizione?.dimensione?.altezza || 0}
                      />
                    </FormField>
                  </div>
                ) : (
                  <div className={styles.dataList}>
                    <div className={styles.dataRow}>
                      <span>{t("Larghezza")}:</span>
                      <strong>{posizione.larghezza} {t("cm")}</strong>
                    </div>
                    <div className={styles.dataRow}>
                      <span>{t("Altezza")}:</span>
                      <strong>{posizione.altezza} {t("cm")}</strong>
                    </div>
                  </div>
                )}  
              </div>
              
              {!posizione.numeroColoriPosizione.fullColor && posizione.numeroColoriPosizione?.numeroColori === posizione.pantoni.length && (
                <div className={cn(styles.section, styles['section--colori'])}>
                  <p className={styles.sectionTitle}>{t('Colori: {0}', [posizione.pantoni.length])}</p>
                  {isEditable ? (
                    <div className={styles.fieldRow}>
                      {posizione.pantoni.map((color, index) => (
                        <FormField
                          key={color.id}
                          label={t("Pantone {0}", [(index + 1).toString()])}
                          status={touched.pantoni?.[index] && errors.pantoni?.[index] ? 'error' : undefined}
                          errorMessage={errors.pantoni?.[index] as string}
                        >
                          <FormInput 
                            id={`${posizione.id}_pantone_${index}`}
                            name={'pantoni[' + index + ']'}
                            value={values.pantoni[index]}
                            onChange={(e) => onValueChange(e, handleChange)}
                            onBlur={handleBlur}
                          />
                        </FormField>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.dataList}>
                      {posizione.pantoni.map((color, index) => (
                        <div className={styles.dataRow} key={color.id}>
                          <span>{t("Pantone {0}", [(index + 1).toString()])}:</span>
                          <strong>{color.pantone}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={styles.row}>
              <div className={cn(styles.section, styles['section--immagini'], loghi.length > 2 ? styles['section--immagini-multi'] : '')}>
                <p className={styles.sectionTitle}>{t('Immagini')}</p>
                <div className={styles.immagini}>
                  <MyAccountImagePreview
                    url={posizione.areaStampa?.immagine?.url}
                    name={posizione.areaStampa?.immagine?.url?.split('/')?.slice(-1)?.[0]}
                    previewName={t("area stampa")}
                    className={styles.immaginiItem}
                  />
                  {loghi.length ? (
                    loghi.map((l, i) => (
                      <MyAccountImagePreview
                        url={l.url}
                        name={l.name}
                        previewName={`${t("logo")} ${i + 1}`}
                        className={cn(styles.immaginiItem, styles['immaginiItem--logo'])}
                        key={`logo-${l.name}`}
                      />
                    ))
                  ) : <></>}
                </div>
              </div>

              {posizione.statoPosizionePersonalizzazione === 'BOZZA_RIFIUTATA' && !!posizione.bozzaDigitaleImage && isEditable && (
                <div className={cn(styles.section, styles['section--prev-draft'])}>
                  <p className={styles.sectionTitle}>{t('Bozza precedente')}</p>
                  <div className={cn(styles.draft)}>
                    <MyAccountImagePreview
                      url={posizione.bozzaDigitaleImage?.url}
                      downloadUrl={posizione.bozzaDigitale?.url}
                      name={posizione.bozzaDigitaleImage?.name}
                      previewName={t("bozza")}
                      size="lg"
                      className={styles.draftPreview} 
                    />
                    <div className={styles.prevDraftFilename}>{posizione.bozzaDigitaleImage.name}</div>
                  </div>
                </div>
              )}

              <div className={cn(styles.section, styles['section--draft'])}>
                <p className={styles.sectionTitle}>{t(posizione.statoPosizionePersonalizzazione === 'BOZZA_RIFIUTATA' && isEditable ? 'Nuova bozza' : 'Bozza')}</p>
                <div className={cn(styles.draft, !values.bozzaDigitaleImage && !bozzaEditor?.url ? styles['draft--empty'] : bozzaEditor?.url ? styles['draft--bozzaEditor'] : '')}>
                  <MyAccountImagePreview
                    url={values.bozzaDigitaleImage?.url || bozzaEditor?.url}
                    downloadUrl={isEditable || bozzaEditor ? null : posizione.bozzaDigitale?.url}
                    name={values.bozzaDigitaleImage?.name || bozzaEditor?.name}
                    previewName={t("bozza")}
                    size="lg"
                    badge={bozzaEditor ? t("Bozza creata dal cliente con editor") : undefined}
                    zoomUrl={bozzaEditor ? bozzaEditor?.url : undefined}
                    onRemove={isEditable ? () => {
                      if (bozzaEditor) {
                        setBozzaEditor(null)
                        // non metto edited a true perchè non voglio che si abiliti il salva
                      }
                      else {
                        setFieldValue('bozzaDigitaleImage', null)
                        setFieldValue('fileBozzaDigitaleImage', null)
                        setEdited(true)
                      }
                    } : undefined}
                    className={styles.draftPreview} 
                  />
                  <div className={styles.draftActions}>
                    {values.bozzaDigitaleImage ? (
                      <div>
                        <div className={styles.draftValue}>
                          <div className={styles.draftFilename}>{values.bozzaDigitaleImage.name}</div>
                          {isEditable && (
                            <Button 
                              variant="ghost" 
                              label={t("Rimuovi")} 
                              ariaLabel={t("Rimuovi bozza")} 
                              icon={<Bin />}
                              icon_pos='right'
                              size="small"
                              colorVariant={'danger'} 
                              onClick={() => {
                                setFieldValue('bozzaDigitaleImage', null)
                                setFieldValue('fileBozzaDigitaleImage', null)
                                setEdited(true)
                              }}
                              className={styles.draftRemoveBig}
                            />
                          )}
                        </div>
                        <p className={styles.helperText}>
                          {t('Formati consentiti: .jpg, .jpeg, .png')}
                        </p>
                      </div>
                    ) : (
                      isEditable ? (
                        <>
                          <div className={styles.uploadBox}>
                            <FormField
                              className={styles.draftButton}
                              status={touched.bozzaDigitaleImage && errors.bozzaDigitaleImage ? 'error' : undefined}
                              errorMessage={errors.bozzaDigitaleImage as string}
                            >
                              <FormFile
                                id={`${posizione.id}_draft`}
                                name="fileBozzaDigitaleImage"
                                value={values.bozzaDigitaleImage}
                                onChange={(event) => {
                                  const nextFile = event.target.files?.[0]

                                  setFieldValue(
                                    'fileBozzaDigitaleImage',
                                    nextFile
                                  )
                                  setFieldValue(
                                    'bozzaDigitaleImage',
                                    nextFile
                                      ? {
                                          url: URL.createObjectURL(nextFile),
                                          name: nextFile.name,
                                        }
                                      : null
                                  )
                                  setFieldTouched('bozzaDigitaleImage', true, false)
                                  setBozzaEditor(null)
                                  setEdited(true)
                                }}
                                variant={'button'}
                                accept=".jpg,.jpeg,.png"
                                buttonProps={{ 
                                  label: t('Carica'), 
                                  variant: 'secondary', 
                                  size: 'small', 
                                  icon: <Upload />, 
                                  iconPos: 'left' 
                                }}
                              />
                            </FormField>
                            <p className={styles.helperText}>
                              {t('Formati consentiti: .jpg, .jpeg, .png')}
                            </p>
                          </div>
                          <div>
                            {!!utente && (
                              <Button
                                variant="secondary"
                                size="small"
                                label={t('Editor')}
                                href={getEditorUrl({
                                  personalizzazioneId: getPersonalizzazioneId(posizione.id),
                                  editorOwnerId: ordine.id,
                                  source: 'caricamento-bozze-digitali-ordine',
                                  scope: EDITOR_SCOPE_ORDER,
                                  positionId: posizione.id,
                                  hash: router.query?.hash,
                                })}
                                icon={<Edit />}
                                icon_pos="left"
                                className={styles.draftButton}
                                disabled={!posizione.editorDisponibile}
                                ariaLabel={
                                  posizione.editorDisponibile
                                    ? t('Apri editor')
                                    : t('Editor non disponibile')
                                }
                              />
                            )}
                          </div>
                        </>
                      ) : <></>
                    )}
                  </div>
                </div>
              </div>
                
              <div className={styles.lastCol}>
                <div className={cn(styles.note, userType !== 'cliente' ? styles['note--withNotaFornitore'] : '')}>
                  <div className={styles.noteRead}>
                    {userType !== 'cliente' && (
                      <div className={cn(styles.section, styles['section--noteFornitore'])}>
                        <div className={styles.sectionTitle}>
                          {t('Note interne per fornitore')}
                        </div>
                        {/* Se c'è stata una bozza rifiutata prendo l'ultima bozza rifiutata, altrimenti prendo la bozza da caricare - questo perchè le bozze rifiutate passano di nuovo a bozza da caricare senza nota dopo la modifica della bozza */}
                        <FlussoPersonalizzazioneNotaValue
                          value={ordine.noteFornitore?.toLocaleLowerCase()}
                          placeholder={t("Nessuna nota inserita per il fornitore")}
                          variant="warning"
                          className={styles.notaClienteValue}
                        />
                      </div>
                    )}
                    <div className={cn(styles.section, styles['section--notaCliente'])}>
                      <div className={styles.sectionTitle}>
                        <span>{t('Nota cliente')}</span>
                        <CronologiaButton onClick={() => setLogOpen(true)} />
                      </div>
                      {/* Se c'è stata una bozza rifiutata prendo l'ultima bozza rifiutata, altrimenti prendo la bozza da caricare - questo perchè le bozze rifiutate passano di nuovo a bozza da caricare senza nota dopo la modifica della bozza */}
                      <FlussoPersonalizzazioneNotaValue
                        value={posizione.log?.filter(l => ['BOZZA_APPROVATA'].includes(l.stato))?.length ? (
                        posizione.log.sort((a, b) => a.created < b.created ? -1 : 1).filter(l => ['BOZZA_APPROVATA'].includes(l.stato))?.[posizione.log.filter(l => ['BOZZA_APPROVATA'].includes(l.stato))?.length - 1].note
                      ) : posizione.log?.filter(l => ['BOZZA_RIFIUTATA'].includes(l.stato))?.length ? (
                        posizione.log.sort((a, b) => a.created < b.created ? -1 : 1).filter(l => ['BOZZA_RIFIUTATA'].includes(l.stato))?.[posizione.log.filter(l => ['BOZZA_RIFIUTATA'].includes(l.stato))?.length - 1].note
                      ) : posizione.log?.filter(l => ['BOZZA_DA_CARICARE'].includes(l.stato))?.length ? (
                        posizione.log.sort((a, b) => a.created < b.created ? -1 : 1).filter(l => ['BOZZA_DA_CARICARE'].includes(l.stato))?.[posizione.log.filter(l => ['BOZZA_DA_CARICARE'].includes(l.stato))?.length - 1].note
                      ) : ''}
                        placeholder={t("Nessuna nota inserita dal cliente")}
                        variant="accent"
                        className={styles.notaClienteValue}
                      />
                    </div>
                  </div>
                  <div className={cn(styles.section, styles['section--notaCaricamento'])}>
                    <p className={styles.sectionTitle}>{t('Nota caricamento')}</p>
                    <div>
                      {isEditable ? (
                      <FormField className={styles.notaCaricamentoField}>
                        <FormTextarea
                          id={`${posizione.id}_nota_caricamento`}
                          name="note"
                          placeholder={t("Aggiungi una nota per il cliente (facoltativa)...")}
                          value={values.note}
                          onChange={(e) => onValueChange(e, handleChange)}
                          onBlur={handleBlur}
                        />
                      </FormField>
                      ) : (
                        <FlussoPersonalizzazioneNotaValue
                          value={posizione.log?.filter(l => ['BOZZA_INVIATA'].includes(l.stato))?.length ? (
                            posizione.log.filter(l => ['BOZZA_INVIATA'].includes(l.stato))?.[posizione.log.filter(l => ['BOZZA_INVIATA'].includes(l.stato))?.length - 1].note
                          ) : ''}
                          placeholder={t("Nessuna nota inserita dal fornitore")}
                          className={styles.noteFornitoreValue}
                        />
                      )}
                      {!!ordine?.linguaUtente && (
                        <div className={styles.noteFooter}>
                          {t('Lingua del cliente')}: <span>{ordine.linguaUtente.nome}</span>
                          <Flag locale={ordine.linguaUtente.isoCode} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {isEditable && (
                  <div className={styles.ctaBox}>
                    <Button 
                      type="submit"
                      variant="primary" 
                      size="medium" 
                      label={t('Salva')} 
                      disabled={!edited || saving || !isValid || (posizione.statoPosizionePersonalizzazione === 'BOZZA_RIFIUTATA' && !values.fileBozzaDigitaleImage) || !!bozzaEditor} // TMP: forziamo l'utente a caricare un nuova bozza o passare dall'editor, quindi se c'è bozzaEditor non è abilitato il Salva 
                      loading={saving}
                      onClick={() => setSubmitAction('default')}
                    />
                    {getNextPosizioneEditabile(ordini, posizione, userType) && (
                      <Button
                        type="submit"
                        variant="primary"
                        size="medium"
                        label={t('Salva e vai alla prossima')}
                        icon={<ArrowRight />}
                        disabled={!edited || saving || !isValid || (posizione.statoPosizionePersonalizzazione === 'BOZZA_RIFIUTATA' && !values.fileBozzaDigitaleImage) || !!bozzaEditor} // TMP: forziamo l'utente a caricare un nuova bozza o passare dall'editor, quindi se c'è bozzaEditor non è abilitato il Salva 
                        onClick={() => setSubmitAction('next')}
                      />
                    )}
                    <Alert 
                      variant="muted" 
                      styleVariant='basic' 
                      icon={<Info />} 
                      placeholder={t(
                        posizione.statoPosizionePersonalizzazione === 'BOZZA_RIFIUTATA' 
                        ? "Carica una nuova bozza per abilitare il salvataggio"
                        : bozzaEditor 
                        ? "Carica una bozza per abilitare il salvataggio, oppure verifica nell'editor quella caricata dal cliente"
                        : "Carica una bozza per abilitare il salvataggio")} 
                      size="xs"
                      className={styles.alertSave} 
                    />
                  </div>
                )}
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <MyAccountStatusLogModal id={`status_log_modal_${posizione.id}`} open={logOpen} onClose={() => setLogOpen(false)} posizione={posizione} userType={userType} />
    </FlussoPersonalizzazionePosizione>
  )
}

export default FlussoPersonalizzazioneCaricamentoBozzePosizione
